/**
 * ReviewInviteModal Component
 *
 * Modal for reviewing invite requests with role-based views:
 *
 * For Admin/Owner users:
 * - Title: "Review Invite Request"
 * - Section 1: Requester Details (Name, Email Address, Role) - who made the request
 * - Section 2: Invite Request Details (Email Address, Requested Role, Request Date, Comment)
 * - Section 3: Request Status (Status badge, Expiration Date)
 * - Footer: Edit Requested Role, Reject Request, Approve Request (for pending) or Close
 *
 * For Viewer/Traffic Viewer (non-admin) users:
 * - Title: "Invite details"
 * - Section 1: Invite Request Details (Email, Requested Role, Invitation Date, Comment)
 * - Section 2: Request Status (Decision badge, Reviewer Comment)
 * - Section 3: Invite Status (if request was approved - shows invitation status)
 * - Footer: Close button only
 *
 * @see Figma designs:
 * - Admin/Owner Pending: node-id=1874-48175
 * - Admin/Owner Non-Pending: node-id=1878-46509
 * - Viewer/Non-Admin: node-id=1867-166838
 */
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import LytModal from '../ui-core/modal/LytModal';
import LytButton from '../library/button/LytButton';
import LytIcon from '../library/icon/LytIcon';
import LytBadge, { BADGE_TYPES } from '../library/badge/LytBadge';
import { ICON_NAMES } from '../library/icon/icon-constants';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from '../styled/theme/typography-utils';
import { formatRole } from '../../utils/role-utils';
import { selectedAgencyJurisdictionsSelector } from '../../store/user-info/user-info.selectors';
import EditRoleModal from './EditRoleModal';
import RequestCommentModal from './RequestCommentModal';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
`;
const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 24px;
`;
const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;
const SectionTitle = styled.h3`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_BOLD)}
    letter-spacing: 0.14px;
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
`;
const SectionDescription = styled.p`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    line-height: 18px;
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
`;
const InfoGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;
const InfoRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 24px;
    min-height: 26px;
`;
const InfoLabelContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    width: 120px;
    flex-shrink: 0;
    padding: 5px 0;
`;
const IconWrapper = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;
const InfoLabel = styled.span`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_MEDIUM)}
    color: ${({ theme }) => theme?.text?.subtle};
`;
const InfoValueContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
    padding: 4px 0;
`;
const InfoValue = styled.span`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    line-height: 18px;
    color: ${({ theme }) => theme?.text?.subtle};
`;
const StatusBadgeContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;
const HeaderBadgeWrapper = styled.div`
    display: inline-flex;
    flex-shrink: 0;
`;
const StatusMessage = styled.span`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_NORMAL)}
    color: ${({ theme }) => theme?.text?.subtle};
`;
const FooterActions = styled.div`
    display: flex;
    gap: 4px;
    align-items: center;
`;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format a date string for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string (e.g., "Jan 15, 2024")
 */
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

/**
 * Calculate days until expiration
 * @param {string} dateString - ISO date string for expiration
 * @returns {number|null} Days until expiration (negative if expired)
 */
const getDaysUntilExpiry = (dateString) => {
    if (!dateString) return null;
    const expiryDate = new Date(dateString);
    if (isNaN(expiryDate.getTime())) return null;
    const now = new Date();
    const diffTime = expiryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
};

/**
 * Get expiration display text
 * @param {string} dateString - ISO date string for expiration
 * @returns {string} Human-readable expiration text
 */
const getExpirationText = (dateString) => {
    const daysUntil = getDaysUntilExpiry(dateString);
    if (daysUntil === null) return 'No expiration date';
    if (daysUntil < 0)
        return `Expired ${Math.abs(daysUntil)} day${
            Math.abs(daysUntil) !== 1 ? 's' : ''
        } ago`;
    if (daysUntil === 0) return 'Expires today';
    return `Expires in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`;
};

/**
 * Determine the status badge type based on request state
 * @param {Object} request - The request object
 * @returns {{ type: string, label: string }} Badge configuration
 */
const getStatusBadge = (request) => {
    const status = request?.status;
    switch (status) {
        case 'rejected':
            return {
                type: BADGE_TYPES.IMPORTANT,
                label: 'Rejected',
            };
        case 'revoked':
            return {
                type: BADGE_TYPES.IMPORTANT,
                label: 'Revoked',
            };
        case 'failed':
            return {
                type: BADGE_TYPES.IMPORTANT,
                label: 'Failed',
            };
        case 'expired':
            return {
                type: BADGE_TYPES.WARNING,
                label: 'Expired',
            };
        case 'accepted':
            return {
                type: BADGE_TYPES.SUCCESS,
                label: 'Accepted',
            };
        case 'approved':
            return {
                type: BADGE_TYPES.SUCCESS,
                label: 'Approved',
            };
        case 'sent':
            return {
                type: BADGE_TYPES.DEFAULT,
                label: 'Pending Response',
            };
        case 'pending':
            break;
        // fall through to expiration check below
        default:
            break;
        // unknown / null — fall through
    }

    // For pending status, check frontend expiration as fallback
    const expiresAt = request?.expiresAt;
    if (expiresAt && new Date(expiresAt) < new Date()) {
        return {
            type: BADGE_TYPES.WARNING,
            label: 'Expired',
        };
    }
    return {
        type: BADGE_TYPES.DEFAULT,
        label: 'Pending Review',
    };
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * ReviewInviteModal - Modal for reviewing and actioning invite requests
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.closeModal - Function to close modal with result (from ModalProvider)
 * @param {Object} props.request - The invite request object to review
 * @param {boolean} props.isAdminOrOwner - Whether the current user is an admin/owner (determines view type)
 * @param {Function} props.onApprove - Callback when request is approved
 * @param {Function} props.onReject - Callback when request is rejected
 * @param {Function} props.onEditRole - Callback when role edit is requested
 */
const ReviewInviteModal = ({
    open = false,
    onClose = undefined,
    closeModal = undefined,
    request = {},
    isAdminOrOwner = true,
    onApprove = undefined,
    onReject = undefined,
    onEditRole = undefined,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submittingAction, setSubmittingAction] = useState(null); // 'approve' or 'reject'
    const [showEditRoleModal, setShowEditRoleModal] = useState(false);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [commentActionType, setCommentActionType] = useState(null); // 'approve', 'reject', or 'editRole'
    const [pendingEditResult, setPendingEditResult] = useState(null); // stores edit role result while comment modal is open

    // Agency jurisdictions from the store — used to resolve IDs to names
    const { list: agencyJurisdictions } = useSelector(
        selectedAgencyJurisdictionsSelector
    );
    if (!open) return null;

    // Requester details (only shown to admin/owner)
    const requesterEmail = request?.invitedByUserEmail || 'N/A';
    const requesterRole = request?.inviterRole || null;

    // Invite request details
    const inviteeEmail = request?.email || 'N/A';
    const requestedRole = request?.role || 'Unknown';
    const requestDate = formatDate(request?.createdAt);
    // Jurisdictions (shown for traffic viewer invites)
    // The request may contain full objects {id, name}, plain ID strings, or a mix.
    // When only IDs are present, resolve names from the agency's jurisdiction list.
    const jurisdictions = request?.jurisdictions || [];
    const jurisdictionDisplay = jurisdictions
        .map((j) => {
            // Already a string (could be an ID or a name)
            if (typeof j === 'string') {
                // Try to resolve the string as an ID against the store
                const match = agencyJurisdictions.find((aj) => aj.id === j);
                return match?.name || j;
            }
            // Object — prefer name, fall back to resolving id
            if (j?.name) return j.name;
            if (j?.id) {
                const match = agencyJurisdictions.find((aj) => aj.id === j.id);
                return match?.name || j.id;
            }
            return null;
        })
        .filter(Boolean)
        .join(', ');
    // Original comment from requester (when they made the request)
    const requesterComment = request?.changeReason || '';
    // Reviewer comment (admin's comment when approving/rejecting)
    const reviewerComment = request?.reviewerComment || '';

    // Status information
    const statusBadge = getStatusBadge(request);
    const expiresAt = request?.expiresAt;
    const expirationText = getExpirationText(expiresAt);
    const isPending = statusBadge.label === 'Pending Review';
    const isApproved = statusBadge.label === 'Approved';
    const isRejected = statusBadge.label === 'Rejected';

    // Determine if we should show action buttons
    // Admin/Owner can approve/deny pending requests
    const canTakeAction = isAdminOrOwner && isPending;

    // Modal title based on user role
    const modalTitle = isAdminOrOwner
        ? 'Review Invite Request'
        : 'Invite details';

    // Get invite status text for non-admin view (after approval)
    const getInviteStatusText = () => {
        if (isApproved) {
            return 'The request was approved and an invitation was issued.';
        }
        if (isRejected) {
            return 'The request was rejected.';
        }
        return 'This invitation request is awaiting review and approval by an administrator';
    };

    // Handle close action
    const handleClose = () => {
        closeModal?.({
            dismissed: true,
        }) || onClose?.();
    };

    // Handle approve action - opens comment modal first
    const handleApprove = () => {
        setCommentActionType('approve');
        setShowCommentModal(true);
    };

    // Handle reject action - opens comment modal first
    const handleReject = () => {
        setCommentActionType('reject');
        setShowCommentModal(true);
    };

    // Handle comment modal close/submit
    const handleCommentModalClose = async (result) => {
        setShowCommentModal(false);

        // If user dismissed the modal, don't proceed
        if (result?.dismissed || result?.cancelled) {
            setCommentActionType(null);
            setPendingEditResult(null);
            return;
        }
        const comment = result?.comment || null;
        setIsSubmitting(true);
        setSubmittingAction(
            commentActionType === 'editRole' ? 'approve' : commentActionType
        );
        try {
            if (commentActionType === 'approve') {
                if (onApprove) {
                    await onApprove(request, comment);
                }
                closeModal?.({
                    action: 'approved',
                    request,
                    comment,
                });
            } else if (commentActionType === 'reject') {
                if (onReject) {
                    await onReject(request, comment);
                }
                closeModal?.({
                    action: 'rejected',
                    request,
                    comment,
                });
            } else if (commentActionType === 'editRole' && pendingEditResult) {
                // The role update already succeeded (fired in EditRoleModal).
                // Now fire the approve endpoint as the second network call.
                if (onApprove) {
                    await onApprove(request, comment);
                }
                closeModal?.({
                    action: 'updated_and_approved',
                    request,
                    newRole: pendingEditResult.newRole,
                    jurisdictions: pendingEditResult.jurisdictions,
                    comment,
                });
            }
        } catch (error) {
            console.error(`Error ${commentActionType}ing request:`, error);
        } finally {
            setIsSubmitting(false);
            setSubmittingAction(null);
            setCommentActionType(null);
            setPendingEditResult(null);
        }
    };

    // Handle edit role action - opens EditRoleModal as nested modal
    const handleEditRole = () => {
        setShowEditRoleModal(true);
    };

    // Handle EditRoleModal close
    const handleEditRoleModalClose = (result) => {
        setShowEditRoleModal(false);

        // If user updated and approved, show the optional comment modal
        // before firing the API call. The actual update happens in
        // handleCommentModalClose when commentActionType === 'editRole'.
        if (result?.action === 'updated_and_approved') {
            setPendingEditResult(result);
            setCommentActionType('editRole');
            setShowCommentModal(true);
            return;
        }

        // Dismissed or cancelled — no further action needed
    };

    // Title badge for header (rendered after title text)
    const headerBadge = (
        <HeaderBadgeWrapper>
            <LytBadge label={statusBadge.label} type={statusBadge.type} />
        </HeaderBadgeWrapper>
    );

    // Modal footer actions - show action buttons only for admin/owner on pending requests
    const footerActions = (
        <FooterActions>
            {canTakeAction && (
                <>
                    <LytButton
                        type="secondary"
                        onClick={handleEditRole}
                        disabled={isSubmitting}>
                        Edit Requested Role
                    </LytButton>
                    <LytButton
                        type="secondary"
                        onClick={handleReject}
                        disabled={isSubmitting}
                        loading={submittingAction === 'reject'}
                        style={{
                            backgroundColor: '#C8322A',
                            color: '#FFFFFF',
                            borderColor: '#C8322A',
                        }}>
                        Reject Request
                    </LytButton>
                    <LytButton
                        type="primary"
                        onClick={handleApprove}
                        disabled={isSubmitting}
                        loading={submittingAction === 'approve'}>
                        Approve Request
                    </LytButton>
                </>
            )}
            {!canTakeAction && (
                <LytButton type="secondary" onClick={handleClose}>
                    Close
                </LytButton>
            )}
        </FooterActions>
    );
    return (
        <>
            <LytModal
                open={open}
                title={modalTitle}
                titleBadge={headerBadge}
                onClose={handleClose}
                actions={footerActions}
                size="medium">
                <ModalContent>
                    {/* Admin/Owner View: Show Requester Details Section */}
                    {isAdminOrOwner && (
                        <Section>
                            <SectionHeader>
                                <SectionTitle>Requester Details</SectionTitle>
                                <SectionDescription>
                                    See who submitted this invite request.
                                </SectionDescription>
                            </SectionHeader>
                            <InfoGrid>
                                <InfoRow>
                                    <InfoLabelContainer>
                                        <IconWrapper>
                                            <LytIcon
                                                name={
                                                    ICON_NAMES.StackedEmailFill
                                                }
                                                size={16}
                                            />
                                        </IconWrapper>
                                        <InfoLabel>Email Address</InfoLabel>
                                    </InfoLabelContainer>
                                    <InfoValueContainer>
                                        <InfoValue>{requesterEmail}</InfoValue>
                                    </InfoValueContainer>
                                </InfoRow>

                                {requesterRole && (
                                    <InfoRow>
                                        <InfoLabelContainer>
                                            <IconWrapper>
                                                <LytIcon
                                                    name={
                                                        ICON_NAMES.BriefcaseFill
                                                    }
                                                    size={16}
                                                />
                                            </IconWrapper>
                                            <InfoLabel>Role</InfoLabel>
                                        </InfoLabelContainer>
                                        <InfoValueContainer>
                                            <InfoValue>
                                                {formatRole(requesterRole)}
                                            </InfoValue>
                                        </InfoValueContainer>
                                    </InfoRow>
                                )}
                            </InfoGrid>
                        </Section>
                    )}

                    {/* Invite Request Details Section - shown to all, different description for non-admin */}
                    <Section>
                        <SectionHeader>
                            <SectionTitle>Invite Request Details</SectionTitle>
                            <SectionDescription>
                                {isAdminOrOwner
                                    ? 'See the details about the person being invited.'
                                    : 'See the details of the person you requested to invite.'}
                            </SectionDescription>
                        </SectionHeader>
                        <InfoGrid>
                            <InfoRow>
                                <InfoLabelContainer>
                                    <IconWrapper>
                                        <LytIcon
                                            name={ICON_NAMES.StackedEmailFill}
                                            size={16}
                                        />
                                    </IconWrapper>
                                    <InfoLabel>Email Address</InfoLabel>
                                </InfoLabelContainer>
                                <InfoValueContainer>
                                    <InfoValue>{inviteeEmail}</InfoValue>
                                </InfoValueContainer>
                            </InfoRow>

                            <InfoRow>
                                <InfoLabelContainer>
                                    <IconWrapper>
                                        <LytIcon
                                            name={ICON_NAMES.BriefcaseFill}
                                            size={16}
                                        />
                                    </IconWrapper>
                                    <InfoLabel>Requested Role</InfoLabel>
                                </InfoLabelContainer>
                                <InfoValueContainer>
                                    <InfoValue>
                                        {formatRole(requestedRole)}
                                    </InfoValue>
                                </InfoValueContainer>
                            </InfoRow>

                            {jurisdictionDisplay && (
                                <InfoRow>
                                    <InfoLabelContainer>
                                        <IconWrapper>
                                            <LytIcon
                                                name={ICON_NAMES.Globe}
                                                size={16}
                                            />
                                        </IconWrapper>
                                        <InfoLabel>
                                            Requested Jurisdiction
                                        </InfoLabel>
                                    </InfoLabelContainer>
                                    <InfoValueContainer>
                                        <InfoValue>
                                            {jurisdictionDisplay}
                                        </InfoValue>
                                    </InfoValueContainer>
                                </InfoRow>
                            )}

                            <InfoRow>
                                <InfoLabelContainer>
                                    <IconWrapper>
                                        <LytIcon
                                            name={ICON_NAMES.CalendarTodayFill}
                                            size={16}
                                        />
                                    </IconWrapper>
                                    <InfoLabel>
                                        {isAdminOrOwner
                                            ? 'Request Date'
                                            : 'Invitation Date'}
                                    </InfoLabel>
                                </InfoLabelContainer>
                                <InfoValueContainer>
                                    <InfoValue>{requestDate}</InfoValue>
                                </InfoValueContainer>
                            </InfoRow>

                            {requesterComment && (
                                <InfoRow>
                                    <InfoLabelContainer>
                                        <IconWrapper>
                                            <LytIcon
                                                name={ICON_NAMES.CommentFill}
                                                size={16}
                                            />
                                        </IconWrapper>
                                        <InfoLabel>Comment</InfoLabel>
                                    </InfoLabelContainer>
                                    <InfoValueContainer>
                                        <InfoValue>
                                            {requesterComment}
                                        </InfoValue>
                                    </InfoValueContainer>
                                </InfoRow>
                            )}
                        </InfoGrid>
                    </Section>

                    {/* Request Status Section - Different content for admin vs non-admin */}
                    <Section>
                        <SectionHeader>
                            <SectionTitle>Request Status</SectionTitle>
                            <SectionDescription>
                                {isAdminOrOwner
                                    ? 'See the current state of this request and who reviewed it'
                                    : 'See the current status of your invite request'}
                            </SectionDescription>
                        </SectionHeader>
                        <InfoGrid>
                            {/* Admin/Owner view: Status with badge and message */}
                            {isAdminOrOwner && (
                                <>
                                    <InfoRow>
                                        <InfoLabelContainer>
                                            <IconWrapper>
                                                <LytIcon
                                                    name={
                                                        ICON_NAMES.BrightnessAlertFill
                                                    }
                                                    size={16}
                                                />
                                            </IconWrapper>
                                            <InfoLabel>Status</InfoLabel>
                                        </InfoLabelContainer>
                                        <InfoValueContainer>
                                            <StatusBadgeContainer>
                                                <HeaderBadgeWrapper>
                                                    <LytBadge
                                                        label={
                                                            statusBadge.label
                                                        }
                                                        type={statusBadge.type}
                                                    />
                                                </HeaderBadgeWrapper>
                                                {isPending && (
                                                    <StatusMessage>
                                                        This invitation request
                                                        is awaiting review and
                                                        approval by an
                                                        administrator
                                                    </StatusMessage>
                                                )}
                                            </StatusBadgeContainer>
                                        </InfoValueContainer>
                                    </InfoRow>

                                    <InfoRow>
                                        <InfoLabelContainer>
                                            <IconWrapper>
                                                <LytIcon
                                                    name={
                                                        ICON_NAMES.CalendarTodayFill
                                                    }
                                                    size={16}
                                                />
                                            </IconWrapper>
                                            <InfoLabel>
                                                Expiration Date
                                            </InfoLabel>
                                        </InfoLabelContainer>
                                        <InfoValueContainer>
                                            <InfoValue>
                                                {expirationText}
                                            </InfoValue>
                                        </InfoValueContainer>
                                    </InfoRow>
                                </>
                            )}

                            {/* Non-admin view: Decision badge and reviewer comment */}
                            {!isAdminOrOwner && (
                                <>
                                    <InfoRow>
                                        <InfoLabelContainer>
                                            <IconWrapper>
                                                <LytIcon
                                                    name={
                                                        ICON_NAMES.BrightnessAlertFill
                                                    }
                                                    size={16}
                                                />
                                            </IconWrapper>
                                            <InfoLabel>Decision</InfoLabel>
                                        </InfoLabelContainer>
                                        <InfoValueContainer>
                                            <StatusBadgeContainer>
                                                <HeaderBadgeWrapper>
                                                    <LytBadge
                                                        label={
                                                            statusBadge.label
                                                        }
                                                        type={statusBadge.type}
                                                    />
                                                </HeaderBadgeWrapper>
                                            </StatusBadgeContainer>
                                        </InfoValueContainer>
                                    </InfoRow>

                                    {reviewerComment && (
                                        <InfoRow>
                                            <InfoLabelContainer>
                                                <IconWrapper>
                                                    <LytIcon
                                                        name={
                                                            ICON_NAMES.CommentFill
                                                        }
                                                        size={16}
                                                    />
                                                </IconWrapper>
                                                <InfoLabel>Comment</InfoLabel>
                                            </InfoLabelContainer>
                                            <InfoValueContainer>
                                                <InfoValue>
                                                    {reviewerComment}
                                                </InfoValue>
                                            </InfoValueContainer>
                                        </InfoRow>
                                    )}
                                </>
                            )}
                        </InfoGrid>
                    </Section>

                    {/* Non-admin view: Invite Status Section (shown when request is approved) */}
                    {!isAdminOrOwner && (isApproved || isPending) && (
                        <Section>
                            <SectionHeader>
                                <SectionTitle>Invite Status</SectionTitle>
                                <SectionDescription>
                                    {getInviteStatusText()}
                                </SectionDescription>
                            </SectionHeader>
                            <InfoGrid>
                                <InfoRow>
                                    <InfoLabelContainer>
                                        <IconWrapper>
                                            <LytIcon
                                                name={
                                                    ICON_NAMES.BrightnessAlertFill
                                                }
                                                size={16}
                                            />
                                        </IconWrapper>
                                        <InfoLabel>Status</InfoLabel>
                                    </InfoLabelContainer>
                                    <InfoValueContainer>
                                        <StatusBadgeContainer>
                                            <HeaderBadgeWrapper>
                                                <LytBadge
                                                    label={
                                                        isPending
                                                            ? 'Pending Response'
                                                            : statusBadge.label
                                                    }
                                                    type={
                                                        isPending
                                                            ? BADGE_TYPES.DEFAULT
                                                            : statusBadge.type
                                                    }
                                                />
                                            </HeaderBadgeWrapper>
                                            {isPending && (
                                                <StatusMessage>
                                                    The invitation has been sent
                                                    and is awaiting the
                                                    invitee&apos;s acceptance.
                                                </StatusMessage>
                                            )}
                                        </StatusBadgeContainer>
                                    </InfoValueContainer>
                                </InfoRow>

                                <InfoRow>
                                    <InfoLabelContainer>
                                        <IconWrapper>
                                            <LytIcon
                                                name={
                                                    ICON_NAMES.CalendarTodayFill
                                                }
                                                size={16}
                                            />
                                        </IconWrapper>
                                        <InfoLabel>Expiration Date</InfoLabel>
                                    </InfoLabelContainer>
                                    <InfoValueContainer>
                                        <InfoValue>{expirationText}</InfoValue>
                                    </InfoValueContainer>
                                </InfoRow>
                            </InfoGrid>
                        </Section>
                    )}
                </ModalContent>
            </LytModal>

            {/* Nested EditRoleModal - rendered directly to avoid modal provider conflict */}
            <EditRoleModal
                open={showEditRoleModal}
                request={request}
                isRequest={true}
                onClose={() =>
                    handleEditRoleModalClose({
                        dismissed: true,
                    })
                }
                closeModal={handleEditRoleModalClose}
                onUpdateAndApprove={async (entity, updateData) => {
                    if (onEditRole) {
                        const result = await onEditRole(request, {
                            newRole: updateData.role,
                            jurisdictions: updateData.jurisdictions,
                        });
                        // If the operation returned failure, throw so EditRoleModal
                        // stays open with the loading spinner stopped.
                        if (result && result.success === false) {
                            throw new Error('Role update failed');
                        }
                    }
                }}
            />

            {/* Nested RequestCommentModal - rendered directly to avoid modal provider conflict */}
            <RequestCommentModal
                open={showCommentModal}
                onClose={() =>
                    handleCommentModalClose({
                        dismissed: true,
                    })
                }
                closeModal={handleCommentModalClose}
            />
        </>
    );
};
ReviewInviteModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    closeModal: PropTypes.func,
    request: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        role: PropTypes.string,
        createdAt: PropTypes.string,
        expiresAt: PropTypes.string,
        requestType: PropTypes.string,
        status: PropTypes.string,
        approvedByUserEmail: PropTypes.string,
        approvedAt: PropTypes.string,
        invitedByUserEmail: PropTypes.string,
        changeReason: PropTypes.string,
        modifiedByUserEmail: PropTypes.string,
        agencyId: PropTypes.string,
        jurisdictions: PropTypes.array,
        // Optional fields from normalizer
        inviterRole: PropTypes.string,
        reviewerComment: PropTypes.string,
        sentAtTs: PropTypes.string,
        acceptedAtTs: PropTypes.string,
    }),
    /** Whether the current user is an admin/owner (determines view type) */
    isAdminOrOwner: PropTypes.bool,
    onApprove: PropTypes.func,
    onReject: PropTypes.func,
    onEditRole: PropTypes.func,
};
export default ReviewInviteModal;
